import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { apiGetRequest } from "helpers/Requests";
import { aiApi } from "store/services/ai.services";
import { URL_SP_AI_WALLET } from "store/services/URL";
import {
  applyTimesheetAiProgressEvent,
  INITIAL_AI_PROGRESS_STATE,
} from "../aiAnalyseStages";
import {
  buildTimesheetRowLabel,
  resolveTimesheetAiErrorMessage,
} from "../utils/timesheetAiUtils";
import {
  AI_PROGRESS_STREAM_CAP,
  runAiProgressFinishSequence,
} from "../utils/useAiProgressDisplay";
import {
  releaseBodyScrollLock,
  releaseBodyScrollLockAfterTransition,
} from "../utils/bodyScrollLock";

/**
 * Wallet confirm → live SSE analyse, shared by Care Watch Re-analyse.
 * Lives next to the timesheet AI modals so the confirm/progress UI cannot
 * drift from the timesheet screen.
 *
 * The timesheet page still owns its own stream (batch selection, per-row
 * status merge). This hook is the single-item flow: one timesheet, then
 * `onComplete`.
 *
 * @param {object} [options]
 * @param {() => void} [options.onComplete] after a successful run
 * @param {(item: object) => object|null} [options.mapItemToRow] shape a
 *   foreign item (Care Watch signal) into the timesheet row the confirm
 *   modal labels
 * @param {boolean} [options.releaseScrollOnSwap] the timesheet page nested
 *   scroll lock needs a hard release when swapping confirm → progress.
 *   Care Watch's overlay lock must not be cleared, so it passes false.
 * @param {string} [options.successMessage]
 * @param {string} [options.invalidItemMessage]
 */
export const useTimesheetAiAnalyseFlow = ({
  onComplete,
  mapItemToRow,
  releaseScrollOnSwap = false,
  successMessage = "Finding refreshed.",
  invalidItemMessage = "This timesheet cannot be analysed.",
} = {}) => {
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    balanceAud: null,
    walletLoading: false,
    walletError: null,
  });
  const [progressModal, setProgressModal] = useState(INITIAL_AI_PROGRESS_STATE);
  const [confirming, setConfirming] = useState(false);
  const [pendingRow, setPendingRow] = useState(null);

  const abortRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const mapItemToRowRef = useRef(mapItemToRow);
  const busyRef = useRef(false);
  const walletReqRef = useRef(0);
  const streamLockRef = useRef(false);
  onCompleteRef.current = onComplete;
  mapItemToRowRef.current = mapItemToRow;

  const streaming = confirming || progressModal.open;

  const closeConfirm = useCallback(() => {
    if (confirming || streamLockRef.current) return;
    walletReqRef.current += 1;
    busyRef.current = false;
    setConfirmModal((prev) => ({ ...prev, open: false }));
    setPendingRow(null);
  }, [confirming]);

  const closeProgress = useCallback(() => {
    busyRef.current = false;
    streamLockRef.current = false;
    setProgressModal(INITIAL_AI_PROGRESS_STATE);
    if (releaseScrollOnSwap) releaseBodyScrollLockAfterTransition();
  }, [releaseScrollOnSwap]);

  const startAnalyse = useCallback(async (item) => {
    const row = mapItemToRowRef.current ? mapItemToRowRef.current(item) : item;
    const timesheetId = Number(row?.id ?? item?.timesheetId ?? item?.id);
    if (!row || !Number.isFinite(timesheetId) || timesheetId <= 0) {
      toast.error(invalidItemMessage);
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;

    const reqId = ++walletReqRef.current;
    setPendingRow({ ...row, id: timesheetId });
    setConfirmModal({
      open: true,
      balanceAud: null,
      walletLoading: true,
      walletError: null,
    });

    try {
      const walletRes = await apiGetRequest(URL_SP_AI_WALLET);
      if (walletReqRef.current !== reqId) return;
      const walletPayload = walletRes?.data?.data ?? walletRes?.data ?? {};
      const wallet = walletPayload?.wallet ?? {};
      const balanceAud = Number(wallet?.balance_aud || 0);
      setConfirmModal((prev) => ({
        ...prev,
        balanceAud,
        walletLoading: false,
        walletError: null,
      }));
    } catch (error) {
      if (walletReqRef.current !== reqId) return;
      setConfirmModal((prev) => ({
        ...prev,
        walletLoading: false,
        walletError:
          error?.message || "Could not load AI credit balance. Try again.",
      }));
    }
  }, [invalidItemMessage]);

  const runStream = useCallback(async (timesheetIds, timesheetRows) => {
    if (streamLockRef.current) return null;
    streamLockRef.current = true;

    setConfirmModal((prev) => ({ ...prev, open: false }));
    if (releaseScrollOnSwap) releaseBodyScrollLock();
    setProgressModal({
      ...INITIAL_AI_PROGRESS_STATE,
      open: true,
      timesheetTotal: timesheetIds.length,
    });
    setConfirming(true);

    const rowById = new Map(
      (timesheetRows || []).map((row) => [Number(row?.id), row])
    );
    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      const complete = await aiApi.runTimesheetAnomalyCheckBatchStream(
        timesheetIds,
        {
          signal: abortController.signal,
          onEvent: (event) => {
            if (event?.type !== "progress") return;
            const row = rowById.get(Number(event.timesheet_id));
            const { title } = buildTimesheetRowLabel(row);
            const nextPercent = Math.min(
              AI_PROGRESS_STREAM_CAP,
              Number(event.percent ?? 0) || 0
            );
            setProgressModal((prev) =>
              applyTimesheetAiProgressEvent(prev, event, {
                nextPercent,
                timesheetLabel: title,
              })
            );
          },
        }
      );

      const payload = complete?.data ?? {};
      const batchPayload = {
        results: payload?.results || [],
        summary: payload?.summary || {},
      };
      const succeeded = Number(batchPayload.summary?.succeeded ?? 0);
      const failedItems = (batchPayload.results || []).filter(
        (result) => result?.success !== true
      );

      if (succeeded === 0 && failedItems.length > 0) {
        const firstFail = failedItems[0];
        const err = new Error(
          resolveTimesheetAiErrorMessage(firstFail, "AI analysis unavailable.")
        );
        err.code = firstFail?.code || "AI_UNAVAILABLE";
        err.toastShown = true;
        await runAiProgressFinishSequence(setProgressModal, {
          error: err.message,
          errorCode: err.code,
        });
        toast.error(err.message);
        throw err;
      }

      await runAiProgressFinishSequence(setProgressModal);
      setProgressModal(INITIAL_AI_PROGRESS_STATE);
      if (releaseScrollOnSwap) releaseBodyScrollLockAfterTransition();
      busyRef.current = false;
      streamLockRef.current = false;
      toast.success(successMessage);
      onCompleteRef.current?.();
      return batchPayload;
    } catch (error) {
      if (error?.code === "CANCELLED") {
        setProgressModal(INITIAL_AI_PROGRESS_STATE);
        if (releaseScrollOnSwap) releaseBodyScrollLockAfterTransition();
        busyRef.current = false;
        streamLockRef.current = false;
        return null;
      }

      setProgressModal((prev) => ({
        ...prev,
        error: resolveTimesheetAiErrorMessage(
          error,
          error?.message || "Failed to run AI anomaly analysis."
        ),
        errorCode: error?.code || null,
        status: error?.status || null,
      }));

      if (error?.code !== "CANCELLED" && !error?.toastShown) {
        toast.error(
          resolveTimesheetAiErrorMessage(
            error,
            error?.message || "Failed to run AI anomaly analysis."
          )
        );
      }
      throw error;
    } finally {
      abortRef.current = null;
      setConfirming(false);
      setPendingRow(null);
    }
  }, [releaseScrollOnSwap, successMessage]);

  const handleConfirm = useCallback(async () => {
    const timesheetId = Number(pendingRow?.id);
    if (!Number.isFinite(timesheetId) || timesheetId <= 0) return;
    if (confirming || streamLockRef.current) return;

    try {
      await runStream([timesheetId], pendingRow ? [pendingRow] : []);
    } catch {
      // Errors are shown in the progress modal and toast.
    }
  }, [confirming, pendingRow, runStream]);

  const handleCancelProgress = useCallback(async () => {
    if (progressModal.error) {
      closeProgress();
      return;
    }

    const confirmed = await swal({
      title: "Cancel AI analyse?",
      text: "Analyse is still in progress. Are you sure you want to cancel?",
      icon: "warning",
      dangerMode: true,
      buttons: ["Keep analysing", "Yes, cancel"],
    });

    if (!confirmed) return;

    abortRef.current?.abort();
    busyRef.current = false;
    streamLockRef.current = false;
    setProgressModal(INITIAL_AI_PROGRESS_STATE);
    setConfirming(false);
    if (releaseScrollOnSwap) releaseBodyScrollLockAfterTransition();
    toast.info("AI analyse cancelled.");
  }, [closeProgress, progressModal.error, releaseScrollOnSwap]);

  return {
    streaming,
    startAnalyse,
    confirmModalProps: {
      isOpen: confirmModal.open,
      onClose: closeConfirm,
      onConfirm: handleConfirm,
      timesheetCount: pendingRow ? 1 : 0,
      timesheets: pendingRow ? [pendingRow] : [],
      balanceAud: confirmModal.balanceAud,
      walletLoading: confirmModal.walletLoading,
      walletError: confirmModal.walletError,
      confirming,
      variant: "single",
    },
    progressModalProps: {
      isOpen: progressModal.open,
      percent: progressModal.percent,
      stage: progressModal.stage,
      label: progressModal.label,
      timesheetCompleted: progressModal.timesheetCompleted,
      timesheetIndex: progressModal.timesheetIndex,
      timesheetTotal: progressModal.timesheetTotal,
      timesheetLabel: progressModal.timesheetLabel,
      error: progressModal.error,
      onClose: closeProgress,
      onCancelRequest: handleCancelProgress,
    },
  };
};
